import type { IceServerPayload } from '@screenshare/protocol';

const DEFAULT_STUN_URLS = ['stun:stun.cloudflare.com:3478'] as const;

export interface IceEnvironment {
  readonly VITE_STUN_URLS?: string;
  readonly VITE_TURN_URLS?: string;
  readonly VITE_TURN_USERNAME?: string;
  readonly VITE_TURN_CREDENTIAL?: string;
}

export interface IceConfigurationResult {
  readonly configuration: RTCConfiguration;
  readonly warnings: readonly string[];
}

export function createIceConfiguration(environment: IceEnvironment): IceConfigurationResult {
  const warnings: string[] = [];
  const stunUrls = parseUrls(environment.VITE_STUN_URLS, ['stun:', 'stuns:'], warnings);
  const turnUrls = parseUrls(environment.VITE_TURN_URLS, ['turn:', 'turns:'], warnings);
  const iceServers: RTCIceServer[] = [
    { urls: stunUrls.length > 0 ? stunUrls : [...DEFAULT_STUN_URLS] },
  ];

  if (turnUrls.length > 0) {
    const username = environment.VITE_TURN_USERNAME?.trim();
    const credential = environment.VITE_TURN_CREDENTIAL?.trim();
    if (username && credential) {
      iceServers.push({ urls: turnUrls, username, credential });
    } else {
      warnings.push('TURN URLs were ignored because username or credential is missing.');
    }
  }

  return { configuration: { iceServers }, warnings };
}

export function withProvisionedIceServers(
  configuration: RTCConfiguration,
  servers: readonly IceServerPayload[],
): RTCConfiguration {
  if (servers.length === 0) return configuration;
  return {
    ...configuration,
    iceServers: servers.map(({ urls, username, credential }) => ({
      urls: [...urls],
      ...(username === undefined ? {} : { username }),
      ...(credential === undefined ? {} : { credential }),
    })),
  };
}

function parseUrls(
  value: string | undefined,
  allowedSchemes: readonly string[],
  warnings: string[],
): string[] {
  if (value === undefined || value.trim() === '') {
    return [];
  }

  const urls = new Set<string>();
  for (const candidate of value.split(',')) {
    const url = candidate.trim();
    if (url === '') {
      continue;
    }
    if (!allowedSchemes.some((scheme) => url.toLowerCase().startsWith(scheme))) {
      warnings.push(`Ignored ICE URL with unsupported scheme: ${redactUrl(url)}`);
      continue;
    }
    urls.add(url);
  }
  return [...urls];
}

function redactUrl(value: string): string {
  const separator = value.indexOf(':');
  return separator < 0 ? '[redacted]' : `${value.slice(0, separator)}:[redacted]`;
}
